import { Injectable, signal, WritableSignal, inject, effect, untracked, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, of, throwError, forkJoin, from, timer, Subscription } from 'rxjs';
import { catchError, map, switchMap, finalize, tap, mergeMap, scan, delayWhen, toArray, concatMap } from 'rxjs/operators';
import { ExternalDocument, DocumentType, ApiResponse } from '@app/types';
import { FileService, UploadResponse } from './file.service';
import { AnalysisService } from './analysis.service';

export interface UploadProgress {
    processed: number;
    total: number;
    currentFolio: string;
    folioStatuses?: Map<string, 'pending' | 'uploading' | 'success' | 'error'>;
}

@Injectable({
    providedIn: 'root',
})
export class ExternalDocumentService {
    private readonly http = inject(HttpClient);
    private readonly fileService = inject(FileService);
    private readonly analysisService = inject(AnalysisService);

    private _externalDocuments: WritableSignal<ExternalDocument[]> = signal([]);
    public readonly externalDocumentsSignal = this._externalDocuments.asReadonly();

    private _isLoading: WritableSignal<boolean> = signal(false);
    public readonly isLoadingSignal = this._isLoading.asReadonly();

    // 1. LA COLA AHORA VIVE AQUÍ (PERSISTENCIA)
    // Mapa: Folio -> Lista de Archivos
    private _uploadQueue: WritableSignal<Map<string, File[]>> = signal(new Map());
    public readonly uploadQueue = this._uploadQueue.asReadonly();

    private readonly API_URL = environment.API_URL;

    // --- NUEVO: Estado de Carga Persistente ---
    // Estas señales viven en el servicio, así que no mueren al cambiar de ruta
    private _uploadProgress: WritableSignal<UploadProgress | null> = signal(null);
    public readonly uploadProgress = this._uploadProgress.asReadonly();

    private _isUploading: WritableSignal<boolean> = signal(false);
    public readonly isUploading = this._isUploading.asReadonly();

    // Guardamos la suscripción aquí para que no se cancele al destruir el componente
    private activeUploadSubscription: Subscription | null = null;

    // --- ESTADO DE BORRADO ---
    private _deletingIds: WritableSignal<Set<string>> = signal(new Set());
    public readonly deletingIds = this._deletingIds.asReadonly();

    private _isDeleting: WritableSignal<boolean> = signal(false);
    public readonly isDeleting = this._isDeleting.asReadonly();

    private activeDeleteSubscription: Subscription | null = null;

    constructor() {
        // Sincronización reactiva: Escuchar cambios en AnalysisService y actualizar items locales
        effect(() => {
            const analysisState = this.analysisService.state();
            const updates = analysisState.documents_status;

            if (updates && Object.keys(updates).length > 0) {
                // Usamos untracked para evitar bucles si _externalDocuments fuera una dependencia
                const currentDocs = untracked(this._externalDocuments);
                const deletingSet = untracked(this._deletingIds);

                let hasChanges = false;

                // Mapear actualizaciones
                const updatedDocs = currentDocs.map(doc => {
                    // Si el documento está en cola de borrado, NO actualizamos su estatus visual (se queda en ELIMINANDO)
                    if (deletingSet.has(doc.id)) return doc;

                    const newStatus = updates[doc.id];
                    // Si hay un estatus nuevo y es diferente al actual
                    if (newStatus && newStatus !== doc.status) {
                        hasChanges = true;
                        // Retornamos una copia con el nuevo estatus
                        return { ...doc, status: newStatus as any };
                    }
                    return doc;
                });

                if (hasChanges) {
                    this._externalDocuments.set(updatedDocs);
                }
            }
        }, { allowSignalWrites: true });
    }

    /**
     * Extrae el folio del nombre del archivo
     * Asume formato "FOLIO_descripcion.ext" o "FOLIO.ext"
     */
    extractFolioFromFilename(filename: string): string {
        const nameWithoutExt = filename.split('.').slice(0, -1).join('.');
        return nameWithoutExt.split('_')[0];
    }

    addFilesToQueue(files: File[]) {
        const currentMap = new Map(this._uploadQueue());

        files.forEach(file => {
            const folio = this.extractFolioFromFilename(file.name);
            if (!currentMap.has(folio)) {
                currentMap.set(folio, []);
            }
            currentMap.get(folio)!.push(file);
        });

        this._uploadQueue.set(currentMap);
    }

    removeFileFromQueue(folio: string, index: number) {
        const currentMap = new Map(this._uploadQueue());
        const files = currentMap.get(folio);
        if (files) {
            files.splice(index, 1);
            if (files.length === 0) currentMap.delete(folio);
            this._uploadQueue.set(currentMap);
        }
    }

    removeFolioFromQueue(folio: string, isManual: boolean = false) {
        const currentMap = new Map(this._uploadQueue());
        if (currentMap.has(folio)) {
            currentMap.delete(folio);
            this._uploadQueue.set(currentMap);

            // Si es borrado manual durante la carga, reducimos el total esperado
            if (isManual && this._isUploading()) {
                this._uploadProgress.update(curr => {
                    if (!curr) return null;
                    return { ...curr, total: Math.max(0, curr.total - 1) };
                });
            }
        }
    }

    clearQueue() {
        this._uploadQueue.set(new Map());
    }

    /**
     * Inicia el borrado masivo en segundo plano.
     */
    public initiateBackgroundDeletion(docIds: string[]) {
        if (docIds.length === 0) return;

        // 1. Actualización Optimista
        this._deletingIds.update(set => {
            const newSet = new Set(set);
            docIds.forEach(id => newSet.add(id));
            return newSet;
        });

        this._isDeleting.set(true);

        // 2. Dividir en Chunks
        const chunkSize = 50;
        const chunks = [];
        for (let i = 0; i < docIds.length; i += chunkSize) {
            chunks.push(docIds.slice(i, i + chunkSize));
        }

        // 3. Procesar Chunks en Serie
        this.activeDeleteSubscription = from(chunks).pipe(
            concatMap(chunkIds => {
                return this.deleteDocumentsApi(chunkIds).pipe(
                    tap(() => {
                        // ÉXITO DEL LOTE
                        this._externalDocuments.update(docs => docs.filter(d => !chunkIds.includes(d.id)));
                        this._deletingIds.update(set => {
                            const newSet = new Set(set);
                            chunkIds.forEach(id => newSet.delete(id));
                            return newSet;
                        });
                    }),
                    catchError(err => {
                        console.error("Error borrando lote:", chunkIds, err);
                        // ERROR: Remover de deletingIds para devolver control al usuario
                        this._deletingIds.update(set => {
                            const newSet = new Set(set);
                            chunkIds.forEach(id => newSet.delete(id));
                            return newSet;
                        });
                        return of(null);
                    })
                );
            }),
            finalize(() => {
                if (this._deletingIds().size === 0) {
                    this._isDeleting.set(false);
                    this.activeDeleteSubscription = null;
                }
            })
        ).subscribe();
    }

    private deleteDocumentsApi(ids: string[]): Observable<any> {
        return this.http.request('delete', `${this.API_URL}/external-documents`, {
            body: { ids: ids }
        });
    }

    loadDocuments(forceRefresh: boolean = true, silent: boolean = false): Observable<ExternalDocument[]> {
        if (!silent) this._isLoading.set(true);

        const body = { filters: {}, limit: 1000, offset: 0 };
        return this.http.post<ApiResponse<ExternalDocument>>(`${this.API_URL}/external-documents/read`, body).pipe(
            map(response => {
                const documents = response.data.map(doc => ({
                    ...doc,
                    createdAt: new Date(doc.createdAt),
                    updatedAt: new Date(doc.updatedAt),
                }));
                this._externalDocuments.set(documents);
                return documents;
            }),
            catchError(err => {
                console.error('Error fetching external documents from API', err);
                this._externalDocuments.set([]);
                return throwError(() => err);
            }),
            finalize(() => {
                if (!silent) this._isLoading.set(false);
            })
        );
    }

    /**
     * Sube archivos y crea un documento para un solo folio
     * @private
     */
    private uploadAndCreateSingleDocument(folio: string, files: File[], documentType: DocumentType): Observable<any> {
        const pathPrefix = folio;

        return this.fileService.uploadFiles(files, pathPrefix, documentType).pipe(
            switchMap((uploadResult: UploadResponse) => {
                if (uploadResult.failed.length > 0) {
                    const failedFiles = uploadResult.failed.map(f => f.original_name).join(', ');
                    return throwError(() => new Error(`Fallo al subir: ${failedFiles}`));
                }

                const documentPayload = {
                    folio: folio,
                    document_type: documentType,
                    files: uploadResult.successful.map(f => ({
                        fileName: f.original_name,
                        url: f.url,
                        contentType: f.contentType,
                        size: f.size,
                    })),
                };

                return this.http.post<ApiResponse<ExternalDocument>>(`${this.API_URL}/external-documents`, [documentPayload]);
            })
        );
    }

    /**
     * Procesa una subida masiva de archivos con concurrencia controlada
     * Agrupa automáticamente los archivos por folio y los procesa en lotes de 5
     * @param allFiles Array de todos los archivos a subir
     * @param documentType Tipo de documento
     * @returns Observable que emite el progreso de la subida
     */
    /**
     * Toma los archivos directamente de la señal _uploadQueue
     */
    public initiateQueueUpload(documentType: DocumentType): void {
        const queueMap = this._uploadQueue();

        if (queueMap.size === 0) return;
        if (this._isUploading()) {
            console.warn("Ya hay una carga en progreso.");
            return;
        }

        this._isUploading.set(true);

        const uploadTasks = Array.from(queueMap.entries()).map(([folio, files]) => ({ folio, files }));

        this._uploadProgress.set({
            processed: 0,
            total: uploadTasks.length,
            currentFolio: 'Iniciando...',
            folioStatuses: new Map()
        });

        let processedCount = 0;

        this.activeUploadSubscription = from(uploadTasks).pipe(
            mergeMap((task) => {
                // Chequear si el folio sigue en la cola (no fue borrado manualmente)
                if (!this._uploadQueue().has(task.folio)) {
                    return of(null);
                }

                const currentTotal = this._uploadProgress()?.total || 0;
                this.updateProgressStatus(task.folio, 'uploading', processedCount, currentTotal);

                const minDelay = 1500;
                const startTime = Date.now();

                return this.uploadAndCreateSingleDocument(task.folio, task.files, documentType).pipe(
                    delayWhen(() => {
                        const elapsed = Date.now() - startTime;
                        const remaining = Math.max(0, minDelay - elapsed);
                        return timer(remaining);
                    }),
                    tap(() => {
                        processedCount++;
                        this.removeFolioFromQueue(task.folio, false); // false = borrado interno (éxito)
                        const freshTotal = this._uploadProgress()?.total || 0;
                        this.updateProgressStatus(task.folio, 'success', processedCount, freshTotal);
                    }),
                    catchError(err => {
                        console.error(`Error folio ${task.folio}:`, err);
                        processedCount++; // Contamos el error como procesado
                        const freshTotal = this._uploadProgress()?.total || 0;
                        this.updateProgressStatus(task.folio, 'error', processedCount, freshTotal);
                        return of(null);
                    })
                );
            }, 5),
            finalize(() => {
                this.loadDocuments(true).subscribe();
            })
        ).subscribe({
            complete: () => {
                setTimeout(() => {
                    this._isUploading.set(false);
                    this._uploadProgress.set(null);
                    this.activeUploadSubscription = null;

                    // --- NUEVO: AUTO-DISPARAR ANÁLISIS ---
                    console.log("🚀 Carga masiva finalizada. Disparando análisis automático...");

                    // Llamamos al endpoint de análisis completo
                    // Nota: Usamos 'QWEN' o 'GPU' como procesador por defecto
                    this.analysisService.startFullAnalysis('GPU').subscribe({
                        next: () => {
                            console.log("✅ Análisis automático iniciado exitosamente.");
                        },
                        error: (err) => console.error("❌ Error al auto-iniciar análisis:", err)
                    });

                }, 1000);
            }
        });
    }

    private updateProgressStatus(folio: string, status: 'pending' | 'uploading' | 'success' | 'error', processed: number, total: number) {
        this._uploadProgress.update(prev => {
            if (!prev) return { processed, total, currentFolio: folio, folioStatuses: new Map([[folio, status]]) };
            const newStatuses = new Map(prev.folioStatuses);
            newStatuses.set(folio, status);
            return {
                ...prev,
                processed: processed,
                total: total,
                currentFolio: folio,
                folioStatuses: newStatuses
            };
        });
    }

    uploadAndCreateDocuments(filesByFolio: Map<string, File[]>, documentType: DocumentType): Observable<any> {
        this._isLoading.set(true);

        if (filesByFolio.size === 0) {
            this._isLoading.set(false);
            return of([]);
        }

        const allUploads$: Observable<any>[] = [];

        filesByFolio.forEach((files, folio) => {
            const uploadAndRegister$ = this.uploadAndCreateSingleDocument(folio, files, documentType);
            allUploads$.push(uploadAndRegister$);
        });

        return forkJoin(allUploads$).pipe(
            finalize(() => {
                this._isLoading.set(false);
                this.loadDocuments(true).subscribe(); // Refrescar la lista en la página de procesamiento
            })
        );
    }

    // --- NUEVOS MÉTODOS PARA ACCIONES MASIVAS ---

    /**
     * Elimina múltiples documentos y sus entidades relacionadas.
     * Usa el endpoint de batch del backend.
     */
    /**
     * Replaced by initiateBackgroundDeletion + deleteDocumentsApi
     */
    deleteDocuments(documentIds: string[]): Observable<any> {
        // Deprecated or redirect
        console.warn("Use initiateBackgroundDeletion instead");
        return this.deleteDocumentsApi(documentIds);
    }

    /**
     * Reinicia el análisis para múltiples documentos.
     * Orquesta múltiples llamadas individuales en paralelo (concurrencia controlada).
     */
    resetDocuments(documentIds: string[]): Observable<any> {
        if (documentIds.length === 0) return of(null);

        // No activamos spinner global aquí para permitir feedback custom en el componente si se desea,
        // o el componente puede activar su propio estado de carga.

        return from(documentIds).pipe(
            mergeMap(id => this.resetDocument(id).pipe(
                catchError(err => {
                    console.error(`Error resetting document ${id}`, err);
                    return of(null);
                })
            ), 5), // Concurrencia de 5
            toArray(), // Esperar a que todos terminen y emitir una sola vez
            finalize(() => this.loadDocuments(true).subscribe()) // Refrescar al final
        );
    }

    /**
     * Encola múltiples documentos específicos para análisis.
     * Orquesta múltiples llamadas individuales.
     */
    analyzeDocuments(documentIds: string[], processor: string = 'GPU'): Observable<any> {
        // Filtrar los que se están borrando para no analizarlos
        const validIds = documentIds.filter(id => !this._deletingIds().has(id));
        if (validIds.length === 0) return of(null);

        return from(validIds).pipe(
            mergeMap(id => this.analyzeDocument(id, processor).pipe(
                catchError(err => {
                    console.error(`Error analyzing document ${id}`, err);
                    return of(null);
                })
            ), 5), // Concurrencia de 5
            toArray(), // Esperar a que todos terminen y emitir una sola vez
            finalize(() => this.loadDocuments(true).subscribe())
        );
    }

    triggerAnalysis(documentId: string): Observable<any> {
        this._isLoading.set(true);
        return this.http.post(`${this.API_URL}/external-documents/analyze-all`, {}).pipe(
            switchMap(() => this.loadDocuments(true)), // Recargar la lista para ver el cambio de estado
            finalize(() => this._isLoading.set(false))
        );
    }

    analyzeDocument(documentId: string, processor?: string): Observable<any> {
        // No activamos spinner global para no ocultar la UI
        return this.http.post(`${this.API_URL}/external-documents/${documentId}/analyze`, { processor }).pipe(
            tap((response: any) => {
                // --- CORRECCIÓN: ELIMINAR ACTUALIZACIÓN OPTIMISTA ---
                // Si el backend dice "queued", no ponemos EN_PROCESO.
                // Dejamos que el polling haga su trabajo.
                console.log(`Documento ${documentId} solicitud enviada. Respuesta:`, response);
            })
        );
    }

    resetDocument(documentId: string): Observable<any> {
        // No activamos spinner global para no ocultar la UI
        return this.http.post(`${this.API_URL}/external-documents/${documentId}/reset`, {}).pipe(
            tap(() => {
                // Actualización optimista
                this._externalDocuments.update(docs => docs.map(d => {
                    if (d.id === documentId) return { ...d, status: 'PENDIENTE_ANALISIS', ia_retries: 0 };
                    return d;
                }));
            })
        );
    }

    deleteDocument(documentId: string): Observable<any> {
        return this.deleteDocuments([documentId]);
    }

    removeFile(documentId: string, fileIdentifier: string): Observable<any> {
        return this.http.delete(`${this.API_URL}/external-documents/${documentId}/files`, {
            body: { file_identifier: fileIdentifier }
        }).pipe(
            tap(() => {
                // Actualizar el signal localmente
                this._externalDocuments.update(docs => docs.map(doc => {
                    if (doc.id === documentId) {
                        return {
                            ...doc,
                            files: doc.files.filter(f => f.url !== fileIdentifier && f.path !== fileIdentifier) // Try matching both
                        };
                    }
                    return doc;
                }));
            })
        );
    }

    getDownloadUrl(documentId: string, fileIdentifier: string): Observable<{ download_url: string }> {
        return this.http.post<{ data: any, message: string }>(`${this.API_URL}/external-documents/${documentId}/files/download`, {
            file_identifier: fileIdentifier
        }).pipe(
            map(response => {
                // CORRECCIÓN: El backend devuelve la data dentro de un array [ { download_url: ... } ]
                // Debemos extraer el primer elemento si es un array.
                if (Array.isArray(response.data) && response.data.length > 0) {
                    return response.data[0];
                }
                // Fallback por si la estructura cambia o ya es un objeto
                return response.data;
            })
        );
    }
}